/**
 * Store: Cart
 * -----------------------------------------------------------------------------
 * Cart state and events.
 * - Only JSDOC custom parameters, ignore module state or object.
 *
 */

import useOverlaysStore from './overlays';
import useDiscountCampaignsStore from './discount-campaigns';
import useToastNotificationsStore from './toast-notifications';
import { defineStore, mapState } from 'pinia';
import {
  getFreebiesWithChangedQuantity,
  getInactiveFreebies,
} from '~/helpers/freebies';

import { getCampaignCode, getTypes, isOrderCode } from '~/helpers/discounts';

const useCartStore = defineStore('cart', {
  state: () => ({
    history: [],
    response: {
      attributes: {},
      cart_level_discount_applications: [],
      currency: null,
      discount_codes: [],
      item_count: 0,
      items_subtotal_price: 0,
      items: [],
      note: null,
      original_total_price: 0,
      requires_shipping: false,
      token: null,
      total_discount: 0,
      total_price: 0,
      total_weight: 0,
    },
    bundleItemUpdate: [],
    depositItemUpdate: [],
    status: {
      items: [],
      label: 'loading',
    },
    removeOrderCode: false,
  }),

  getters: {
    /**
     * Get cart items that aren't excluded.
     * - Also filters out free gift items.
     * @returns {Array<Object>}
     */
    getIncludedCartItems() {
      const excluded = {
        property: 'gift_card',
        value: true,
      };

      return this.response.items
        .filter((item) => {
          const property = item[excluded.property];
          return !(Boolean(property) === excluded.value);
        })
        .filter((item) => {
          const freeGift = Object.keys(item.properties).some((key) => {
            return key === cnvs.discount.freebies.property;
          });

          return !freeGift;
        });
    },

    /**
     * Get cart total of items that aren't excluded.
     * @returns {Number}
     */
    getIncludedCartTotal() {
      const cartItems = this.getIncludedCartItems;

      let totalPrice = 0;

      cartItems.forEach((item) => {
        totalPrice += item.line_price;
      });

      return totalPrice;
    },

    getCartProductLimitReached() {
      return (
        cnvs.product.maxCartProducts > 0 &&
        this.response.item_count >= cnvs.product.maxCartProducts
      );
    },

    getCartItemLimitReached() {
      return (
        cnvs.product.maxLineItems > 0 &&
        this.response.items.length >= cnvs.product.maxLineItems
      );
    },
  },

  computed: {
    /**
     * Map Pinia state.
     */
    ...mapState(useDiscountCampaignsStore, ['validNativeCode']),
  },

  actions: {
    /**
     * Actions cart requests.
     * @param {Object} payload - Action payload.
     * @param {String} payload.action - `add`, `clear`, `update`, or `change`.
     * @param {String} payload.body - Stringified body of request.
     * @param {Number} [payload.itemsLeft] - Items left in queue, only set
     * response when no more items are left in queue.
     * @returns {Promise}
     */
    actionCartUpdate({ action, body, itemsLeft = 0 } = {}) {
      const discountCampaignsStore = useDiscountCampaignsStore();
      return new Promise(async (resolve, reject) => {
        try {
          let response = await fetch(cnvs.routes.cart[action], {
            body,
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
          });

          const responseOk = response.ok;
          response = await response.json();

          /**
           * Get cart response even if there are errors.
           * - If you try to add more quantity than available inventory it will
           *   throw an error, but still add the maximum it can so cart needs to
           *   update regardless.
           */
          if (!itemsLeft) {
            await this.setResponse();
          }

          if (!responseOk) {
            this.status = {
              items: [],
              label: 'error',
            };
            reject(response);
            return;
          }

          if (response.items.length === 0) {
            for (const item of response.discount_codes) {
              const code = item.code.toUpperCase();

              if (getCampaignCode(code) !== code) {
                discountCampaignsStore.setRemoveCode(code);
              }
            }
          }

          resolve(response);
        } catch (error) {
          this.status = {
            items: [],
            label: 'error',
          };
          reject(error?.description || error);
        }
      });
    },

    getCartAttributeCode() {
      return this.response.attributes?.discount_codes
        ? this.response.attributes.discount_codes.split(':')[0]
        : '';
    },

    booksInCart() {
      return this.response.items.filter((item) => item.product_type === 'book');
    },

    depositInCart() {
      return this.response.items.filter(
        (item) => item.product_type === 'deposit',
      );
    },
    /**
     * Adds item(s) to the cart.
     * @param {Array} items - Items to add to cart.
     * @param {Number} items[].id - Item variant ID.
     * @param {Object} [items[].properties] - Object of key: value pairs of
     * properties to add.
     * @param {Number} items[].quantity - Quantity to add.
     * @returns {Promise}
     */
    addItem(items) {
      const overlaysStore = useOverlaysStore();
      const discountCampaignStore = useDiscountCampaignsStore();

      const alreadyInCart = items.filter((item) => {
        return this.response.items.some((cartItem) => {
          return cartItem.id === item.id;
        });
      });

      return new Promise(async (resolve, reject) => {
        if (
          (cnvs.product.maxCartProducts > 0 &&
            this.response.item_count >= cnvs.product.maxCartProducts) ||
          (alreadyInCart.length === 0 &&
            cnvs.product.maxLineItems > 0 &&
            this.response.items.length >= cnvs.product.maxLineItems)
        ) {
          overlaysStore.open({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          });
          reject(new Error('Cart limit reached'));
        } else {
          try {
            this.status = {
              items: items.map((item) => item.id),
              label: 'adding',
            };
            const enrichedItems = this.addItemProperties(items);
            const response = await this.actionCartUpdate({
              action: 'add',
              body: JSON.stringify({ items: enrichedItems }),
            });

            if (items.find((item) => item.bundle)) {
              await this.setBundleInformation();
            }
            discountCampaignStore.refreshCampaignThresholds();
            await discountCampaignStore.loadFreeProducts();
            await this.checkAddFreebies();
            await this.checkRemoveFreebies();
            resolve(response);
          } catch (error) {
            this.bundleItemUpdate = [];
            this.depositItemUpdate = [];
            reject(error instanceof Error ? error : new Error(error));
          }
        }

        // Check if there are books in the cart and if there is an order discount code
        // If so, we set the removeOrderCode state to true
        // This is to prevent order discount codes from being applied to book products
        const booksInCart = this.response.items.filter((item) => {
          return item.product_type === 'book';
        });

        const depositInCart = this.response.items.filter((item) => {
          return item.product_type === 'deposit';
        });

        const orderCode = this.response.discount_codes.find((code) =>
          isOrderCode(code.code),
        );

        if (
          (booksInCart.length && orderCode) ||
          (depositInCart.length && orderCode)
        ) {
          this.setRemoveOrderCode(true);
        }
      });
    },

    setRemoveOrderCode(state) {
      this.removeOrderCode = state;
    },

    checkOrderDiscountCode() {
      const orderCode = this.response.discount_codes.find((code) =>
        isOrderCode(code.code),
      );

      if (!orderCode) {
        return;
      }

      if (this.response.total_price === 0 && this.response.items.length === 0) {
        this.setRemoveOrderCode(true);
      }
    },

    addItemProperties(items) {
      return items.map((item) => {
        if (item.hasLocalizedImage) {
          item.properties = {
            ...item.properties,
            __has_localized_image: true,
          };
        }
        return item;
      });
    },

    /**
     * Clears the cart of all items.
     * - Cart attributes and cart note are not affected.
     * @returns {Promise}
     */
    clearCart() {
      return new Promise(async (resolve, reject) => {
        try {
          this.status = {
            items: this.response.items.map((item) => item.key),
            label: 'removing',
          };
          const response = await this.actionCartUpdate({
            action: 'clear',
          });
          resolve(response);
        } catch (error) {
          reject(error instanceof Error ? error : new Error(error));
        }
      });
    },

    /**
     * Clears cart attributes and note.
     * @returns {Promise}
     */
    clearCartAttributes() {
      return new Promise(async (resolve, reject) => {
        try {
          this.status = {
            items: [],
            label: 'updating',
          };
          const attributes = {};

          Object.keys(this.response.attributes).forEach((key) => {
            attributes[key] = null;
          });

          const response = await this.actionCartUpdate({
            action: 'update',
            body: JSON.stringify({
              attributes,
              note: '',
            }),
          });
          resolve(response);
        } catch (error) {
          reject(error);
        }
      });
    },

    /**
     * Adds new event to history.
     * - Latest event is added to the start of the history array.
     * @param {Object} payload - Action payload.
     * @param {Object} payload.newCart - Updated cart.
     * @param {Object} payload.oldCart - Cart before update.
     */
    setHistory({ newCart, oldCart }) {
      let action = false;

      switch (this.status.label) {
        case 'adding':
          action = 'addToCart';
          break;

        case 'removing':
        case 'clearing':
          action = 'removeFromCart';
          break;
      }

      if (!action) {
        return;
      }

      /**
       * Go through new cart and find new or changed items.
       * - Line item property changes are ignored.
       */
      const items = [];

      newCart.items.forEach((newCartItem) => {
        const newItem = { ...newCartItem };

        const oldItem = oldCart.items.find((item) => {
          return item.key === newItem.key;
        });

        /**
         * If new item doesn't exist in old cart then it's been added.
         */
        if (!oldItem) {
          items.push(newItem);
          return;
        }

        if (oldItem.quantity === newItem.quantity) {
          return;
        }

        /**
         * If new item's quantity doesn't match, find the difference and add to
         * items array.
         */
        const difference = newItem.quantity - oldItem.quantity;
        newItem.quantity = difference;

        items.push(newItem);
      });

      /**
       * Go through old cart and see if any don't exist in new cart.
       * - This means the item has been removed.
       */
      oldCart.items.forEach((oldItem) => {
        const newItem = newCart.items.find((item) => {
          return item.key === oldItem.key;
        });

        if (newItem) {
          return;
        }

        items.push(oldItem);
      });

      const event = {
        action,
        items,
        totalPriceChange: newCart.total_price - oldCart.total_price,
        totalQuantityChange: newCart.item_count - oldCart.item_count,
      };

      this.history.unshift = event;
    },

    setBundleInformation() {
      return new Promise(async (resolve, reject) => {
        const bundleProducts = this.response.items.filter((item) => {
          return item.product_type === 'bundle';
        });

        if (!bundleProducts.length > 0) {
          resolve();
          return;
        }

        let bundleInfo = '';
        let name = '';
        const updatedItems = [];

        bundleProducts.forEach((bundle) => {
          bundleInfo += `${bundle.product_title}:${bundle.product_id}:qty:${bundle.quantity}, `;
          name += bundle.product_title;
          updatedItems.push(bundle.id);
        });

        const attributeUpdate =
          bundleProducts.length === 0
            ? {
                attributes: { bundle_info: null },
                name,
              }
            : {
                attributes: { bundle_info: bundleInfo },
                name,
              };

        try {
          await this.updateCartAttributes(attributeUpdate);
          this.bundleItemUpdate =
            bundleProducts.length > 0
              ? this.bundleItemUpdate.filter(
                  (item) => !updatedItems.includes(item),
                )
              : [];
          resolve();
        } catch (error) {
          this.bundleItemUpdate =
            bundleProducts.length > 0
              ? this.bundleItemUpdate.filter(
                  (item) => !updatedItems.includes(item),
                )
              : [];
          reject(error);
        }
      });
    },

    async checkAddFreebies() {
      const discountCampaignsStore = useDiscountCampaignsStore();
      const addedProducts = [];
      const depositVariantIds = [];
      let depositAmount = 0;

      const newActiveThresholds = discountCampaignsStore.thresholds.filter(
        (threshold) => {
          const oldState = discountCampaignsStore.oldThresholds.find(
            (oldThreshold) => {
              return oldThreshold.handle === threshold.handle;
            },
          );

          return threshold.active && threshold.autoAdd && !oldState?.active;
        },
      );

      this.oldThresholds = this.thresholds;

      const itemsToAdd = newActiveThresholds
        .map((threshold) => {
          const freeProduct = threshold.freeProducts.find((product) => {
            return (
              product.selectedOrFirstAvailableVariant.id.toString() ===
              threshold.variantIds[0]
            );
          });

          const selectedVariant = freeProduct.selectedOrFirstAvailableVariant;

          const availableState = selectedVariant
            ? selectedVariant.availableState
            : freeProduct.availableState;

          if (availableState !== 'available') {
            return false;
          }

          // check if item is already in cart, e.g. added in checkout
          const alreadyInCart = this.response.items.filter((item) => {
            return (
              item.properties[cnvs.discount.freebies.property] ===
                threshold.handle &&
              item.id.toString() === threshold.variantIds[0]
            );
          });

          if (alreadyInCart.length > 0) {
            return false;
          }

          if (
            selectedVariant.canDeposit === 'true' &&
            cnvs.store.country === 'DE'
          ) {
            depositVariantIds.push(selectedVariant.id);
            depositAmount += parseInt(selectedVariant.canDepositAmount);
          }

          addedProducts.push(freeProduct);

          return {
            properties: {
              [cnvs.discount.freebies.property]: threshold.handle,
            },
            quantity: 1,
            id: threshold.variantIds[0],
          };
        })
        .filter(Boolean);

      if (itemsToAdd.length > 0) {
        this.setDepositItemUpdate([
          ...this.depositItemUpdate,
          ...depositVariantIds,
        ]);

        if (depositAmount > 0) {
          const depositProduct = {
            id: cnvs.product.depositProductID,
            quantity: depositAmount,
          };
          itemsToAdd.push(depositProduct);
        }

        await this.addItem(itemsToAdd);

        this.setDepositItemUpdate(
          this.depositItemUpdate.filter(
            (id) => !depositVariantIds.includes(id),
          ),
        );
      }

      const toastNotificationsStore = useToastNotificationsStore();

      addedProducts.forEach((product) => {
        toastNotificationsStore.addToast({
          productTitle: product.title,
          id: 'freebie-add-success',
          type: 'success',
        });
      });
    },

    async checkRemoveFreebies() {
      const discountCampaignsStore = useDiscountCampaignsStore();

      const cartItems = this.response.items;
      let freebiesToRemove = [];

      freebiesToRemove = getInactiveFreebies(
        discountCampaignsStore.thresholds,
        cartItems,
      );

      freebiesToRemove = [
        ...freebiesToRemove,
        ...getFreebiesWithChangedQuantity(
          discountCampaignsStore.thresholds,
          cartItems,
        ),
      ];

      const depositAmountToRemove = freebiesToRemove.reduce(
        (sum, item) => sum + (item.deposit || 0),
        0,
      );

      if (depositAmountToRemove > 0) {
        const depositProduct = cartItems.find(
          (item) => item.id.toString() === cnvs.product.depositProductID,
        );

        freebiesToRemove.push({
          id: depositProduct.key,
          quantity: depositProduct.quantity - depositAmountToRemove,
        });
      }

      if (freebiesToRemove.length) {
        await this.updateItem(freebiesToRemove);
      }
    },

    /**
     * Set cart response after querying cart and custom cart data.
     * @param {Boolean} [onHydration] - Setting response from initial site
     * hydration?
     * @returns {Promise}
     */
    setResponse(onHydration = false) {
      /**
       * Query cart.js and extra data from custom cart template.
       */
      const queue = [
        fetch(`${cnvs.routes.cart.js}?no-cache=${new Date().getTime()}`),
        fetch(
          `${cnvs.routes.cart.url}?view=resource&no-cache=${new Date().getTime()}`,
        ),
      ];

      return new Promise(async (resolve, reject) => {
        try {
          const [cartResponse, extraResponse] = await Promise.all(queue);
          const cart = await cartResponse.json();
          const extra = await extraResponse.json();

          /**
           * Go through each line item and merge extra data.
           */
          const items = cart.items.map((item) => {
            const match = extra.find((extraItem) => {
              return extraItem.key === item.key;
            });

            if (!match) {
              return item;
            }

            return {
              ...item,
              ...match,
            };
          });

          /**
           * Merge cart items and set state.
           */
          const newCart = {
            ...cart,
            items,
          };

          const oldCart = { ...this.response };

          this.response = newCart;

          /**
           * Update history.
           */
          if (!onHydration) {
            this.setHistory({ newCart, oldCart });
          }

          if (onHydration) {
            await this.setBundleInformation();

            // we wait a second to ensure that the cart discounts are fully updated
            setTimeout(() => {
              this.checkShippingThreshold(newCart);
            }, 1000);
          }

          /**
           * Update status.
           */
          this.status = {
            items: [],
            label: this.status.label === 'loading' ? 'loaded' : 'updated',
          };
          resolve();
        } catch (error) {
          reject(error);
          cnvs.ReportError('Failed to set cart response', error);
        }
      });
    },

    /**
     * Check if shipping threshold is met and set free shipping state.
     * - due to the url codes it can happen that shipping state is wrong
     */
    checkShippingThreshold(newCart) {
      const discountCampaignsStore = useDiscountCampaignsStore();

      const appliedCodes = newCart.discount_codes.map((code) => {
        return code.code.toUpperCase();
      });

      const isShipping = appliedCodes.some((code) => {
        return getTypes(code) === 'shipping' && code.applicable;
      });

      discountCampaignsStore.setFreeShipping(isShipping);
    },

    /**
     * Set cart response manually from story.
     * - Should only be used by cart-based stories.
     * @param {Object} response - Manually formatted cart response.
     */
    setStoryResponse(response) {
      this.response = response;
    },

    setDepositItemUpdate(payload) {
      this.depositItemUpdate = payload;
    },

    /**
     * Set status of cart manually from story.
     * - Should only be used by cart-based stories.
     * @param {Object} payload - Action payload.
     * @param {Array} payload.items - Array of item keys being updated.
     * @param {String} payload.label - Status description, can be `loading`,
     * `loaded`, `adding`, `removing`, `clearing`, `updating`, `updated`, or
     * `error`.
     */
    setStoryStatus(payload) {
      this.status = payload;
    },

    /**
     * Updates cart attributes and note.
     * @param {Object} payload - Action payload.
     * @param {Object} [payload.attributes] - Attributes to set.
     * @param {String} [payload.discount] - Discount code to set.
     * @param {String} [payload.note] - Cart note to set.
     * @returns {Promise}
     */
    updateCartAttributes(payload) {
      return new Promise(async (resolve, reject) => {
        try {
          this.status = {
            items: [],
            label: 'updating',
          };
          const response = await this.actionCartUpdate({
            action: 'update',
            body: JSON.stringify(payload),
          });

          resolve(response);
        } catch (error) {
          reject(error);
        }
      });
    },

    /**
     * Updates item(s) in the cart.
     * - Uses /cart/change.js request to only affect items currently in cart.
     * - Item key can be provided directly as a string to remove it.
     * - Or an array of objects each containing id and either properties or
     *   quantity to update the item.
     * @param {Array|String} payload - Action payloads or item key.
     * @param {String} payload[].id - Item key to update.
     * @param {Object} [payload[].properties] - Item properties to update.
     * @param {Number} [payload[].quantity] - Quantity to change item to.
     * @returns {Promise}
     */
    updateItem(payload) {
      let body = '';
      let items = [];
      let label = '';
      let bundleUpdate = false;

      /**
       * Handle string (removes item).
       */
      if (typeof payload === 'string') {
        body = [{ id: payload, quantity: 0 }];
        items = [payload];
        label = 'removing';

        const removedItem = this.response.items.find((item) => {
          return item.key === payload;
        });

        bundleUpdate = removedItem?.product_type === 'bundle';

        if (bundleUpdate && removedItem) {
          this.bundleItemUpdate = [...this.bundleItemUpdate, removedItem.id];
        }

        /**
         * Handle array.
         */
      } else {
        const status = [];

        body = payload.map((object) => {
          const { id, properties } = object;
          let { quantity } = object;
          const field = typeof id === 'string' ? 'key' : 'id';

          /**
           * Find line item being updated.
           * - To find the key of the line item no matter what is passed.
           */
          const lineItem = this.response.items.find((item) => {
            return item[field] === id;
          });

          /**
           * Add status to array based on quantity.
           */
          if (typeof quantity === 'number') {
            if (quantity > lineItem.quantity) {
              status.push('adding');
            } else if (quantity < lineItem.quantity) {
              status.push('removing');
            }
          }

          bundleUpdate = lineItem?.product_type === 'bundle';

          if (bundleUpdate) {
            this.bundleItemUpdate = [...this.bundleItemUpdate, lineItem.id];
          }

          items.push(lineItem.key);

          /**
           * If no quantity then set to current line item quantity.
           * - If other line item has matching properties and id then sum
           *   quantity.
           * - Adds compatibility for when id is id, rather than key.
           */
          if (typeof quantity !== 'number') {
            status.push('updating');

            const matchingItem = this.response.items.find((item) => {
              return item.id === lineItem.id && item.properties === properties;
            });

            if (matchingItem) {
              quantity = lineItem.quantity + matchingItem.quantity;
            } else {
              quantity = lineItem.quantity;
            }
          }

          return { id, properties, quantity };
        });

        /**
         * Go through all statuses to see if they're the same.
         * - Set label if they're all the same.
         * - Set label to `updating` if mixed status.
         */
        const uniqueStatus = [];

        status.forEach((statusState) => {
          if (uniqueStatus.includes(statusState)) {
            return;
          }

          uniqueStatus.push(statusState);
        });

        label = uniqueStatus.length > 1 ? 'updating' : uniqueStatus[0];
      }

      /**
       * Action cart update.
       */
      return new Promise(async (resolve, reject) => {
        try {
          this.status = {
            items,
            label,
          };
          const response = [];
          const queueLength = [...body].length;

          for (let index = 0; index < queueLength; index++) {
            if (index) {
              // eslint-disable-next-line no-await-in-loop
              await new Promise((timeoutResolve) => {
                setTimeout(() => {
                  timeoutResolve();
                }, 1000);
              });
            }

            const item = body.shift();
            // eslint-disable-next-line no-await-in-loop
            const itemResponse = await this.actionCartUpdate({
              action: 'change',
              body: JSON.stringify(item),
              itemsLeft: body.length,
            });

            response.push(itemResponse);
          }

          if (bundleUpdate) {
            await this.setBundleInformation();
          }
          const discountCampaignsStore = useDiscountCampaignsStore();
          discountCampaignsStore.refreshCampaignThresholds();
          await discountCampaignsStore.loadFreeProducts();
          await this.checkAddFreebies();
          await this.checkRemoveFreebies();
          this.checkOrderDiscountCode();
          resolve(response);
        } catch (error) {
          this.bundleItemUpdate = [];
          this.depositItemUpdate = [];
          reject(error);
        }
      });
    },
  },
  persist: {
    pick: ['response'],
  },
});

export default useCartStore;
